import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc, useRpcEvent } from '../api/rpc'
import { Select } from './ui/Select'

export interface WorkspaceRef { id: string; name: string; kind: 'personal' | 'team'; teamId: string | null; role: 'owner' | 'editor' | 'viewer' }

/** The caller's workspaces (personal + teams), refreshed when team membership changes. */
export function useWorkspaces() {
  const [items, setItems] = useState<WorkspaceRef[]>([])
  const reload = useCallback(async () => setItems((await rpc.call<{ items: WorkspaceRef[] }>('workspaces.list')).items), [])
  useEffect(() => { void reload().catch(() => undefined) }, [reload])
  useRpcEvent('teams.updated', () => void reload().catch(() => undefined))
  return { items, reload }
}

/** Workspace selector; renders nothing while the user has only the personal workspace. */
export function WorkspacePicker({ items, value, onChange, writableOnly = false, inline = false, id }: {
  items: WorkspaceRef[]; value: string; onChange(id: string): void; writableOnly?: boolean
  /** Borderless trigger, for a toolbar or a composer bar where the label would be noise. */
  inline?: boolean
  id?: string
}) {
  const { t } = useTranslation()
  const list = writableOnly ? items.filter(w => w.role !== 'viewer') : items
  if (list.length < 2) return null
  const name = (w: WorkspaceRef) => `${w.kind === 'personal' ? t('teams.personal') : `👥 ${w.name}`}${w.role === 'viewer' ? ` (${t('teams.role_viewer')})` : ''}`
  const selected = list.find(w => w.id === (value || list[0].id)) ?? list[0]
  // The accessible name repeats the visible value, like RepoPicker: the trigger shows the workspace
  // name, and a name of only "Thư mục làm việc" would not match it (WCAG 2.5.3).
  return (
    <Select
      id={id} label={`${t('teams.workspace')}: ${name(selected)}`}
      variant={inline ? 'inline' : 'default'} className={inline ? 'min-w-0 max-w-[7rem] sm:max-w-[14rem]' : 'max-w-[14rem]'}
      value={value || list[0].id} onChange={onChange}
      options={list.map(w => ({ value: w.id, label: name(w) }))}
    />
  )
}
